import React, { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { useTheme } from './ThemeProvider';
import { cn } from '@crackpoint/shared/utils';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  fullWidth?: boolean;
  children: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      loading = false,
      fullWidth = false,
      disabled,
      className,
      children,
      ...props
    },
    ref,
  ) => {
    const { theme } = useTheme();

    const baseStyles = {
      fontFamily: theme.typography.fontFamily,
      fontWeight: theme.typography.fontWeight.semibold,
      borderRadius: theme.borders.radius.md,
      border: 'none',
      cursor: disabled || loading ? 'not-allowed' : 'pointer',
      opacity: disabled || loading ? 0.6 : 1,
      transition: `all ${theme.transitions.normal}`,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: theme.spacing.sm,
    };

    const variantStyles: Record<string, React.CSSProperties> = {
      primary: {
        backgroundColor: theme.colours.accent.primary,
        color: theme.colours.text.inverse,
      },
      secondary: {
        backgroundColor: theme.colours.background.elevated,
        color: theme.colours.text.primary,
        border: `2px solid ${theme.colours.accent.primary}`,
      },
      ghost: {
        backgroundColor: 'transparent',
        color: theme.colours.text.primary,
      },
      danger: {
        backgroundColor: theme.colours.accent.error,
        color: theme.colours.text.inverse,
      },
    };

    const sizeStyles = {
      sm: {
        padding: `${theme.spacing.sm} ${theme.spacing.md}`,
        fontSize: theme.typography.fontSize.sm,
      },
      md: {
        padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
        fontSize: theme.typography.fontSize.md,
      },
      lg: {
        padding: `${theme.spacing.md} ${theme.spacing.xl}`,
        fontSize: theme.typography.fontSize.lg,
      },
    };

    return (
      <button
        ref={ref}
        disabled={disabled || loading}
        className={cn(
          'button',
          variant === 'primary' && 'button--primary',
          variant === 'secondary' && 'button--secondary',
          variant === 'ghost' && 'button--ghost',
          variant === 'danger' && 'button--danger',
          size === 'sm' && 'button--sm',
          size === 'md' && 'button--md',
          size === 'lg' && 'button--lg',
          fullWidth && 'button--full-width',
          loading && 'button--loading',
          className,
        )}
        style={{
          ...baseStyles,
          ...variantStyles[variant],
          ...(fullWidth ? { width: '100%' } : {}),
          ...sizeStyles[size],
        }}
        {...props}
      >
        {loading && <span className="spinner" aria-hidden="true" />}
        {children}
      </button>
    );
  },
);

Button.displayName = 'Button';
